<template>
  <div
    ref="reference"
    class="relative w-fit"
    v-bind="$attrs"
    @click="handleClick"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <slot
      :active="isPanelOpen"
      :toggle="toggle"
      :open="open"
      :close="close"
      name="trigger"
    />
  </div>
  <Teleport to="body">
    <Transition
      @enter="emit('show')"
      @leave="emit('hide')"
      @after-enter="onPanelFullyLoaded"
    >
      <div
        v-if="isPanelOpen"
        ref="floating"
        class="panel"
        :style="[floatingStyles, { zIndex: zIndexValue }]"
        @mouseenter="handlePanelMouseEnter"
        @mouseleave="handlePanelMouseLeave"
      >
        <div
          v-if="showArrow"
          ref="floatingArrow"
          class="absolute z-[101] size-[10px] rotate-45 border border-[--v3-root-surface-border] bg-white"
          :class="{
            'border-b-0 border-r-0': placementPos === 'bottom',
            'border-l-0 border-t-0': placementPos === 'top'
          }"
          :style="{
            left: leftPosition,
            ...yPosition,
          }"
        />
        <div>
          <slot
            :active="isPanelOpen"
            :toggle="toggle"
            :open="open"
            :close="close"
            name="panel"
          />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import type { Placement } from '@floating-ui/vue';
import { arrow, autoUpdate, flip, offset as floatOffset, shift, useFloating } from '@floating-ui/vue';
import { onClickOutside } from '@vueuse/core';
import { computed, ref } from 'vue';



defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(defineProps<{
  /** Will the popover be active by clicking inside the trigger */
  clickable?: boolean;
  /** Will the popover be active by hovering over the trigger */
  hoverable?: boolean;
  /** Delay in milliseconds before showing the popover on hover */
  showDelay?: number;
  /** Delay in milliseconds before hiding the popover when mouse leaves */
  hideDelay?: number;
  offset?: number;
  placement?: Placement;
  showArrow?: boolean;
  /** z-index for the panel container */
  zIndex?: number;
}>(), {
  clickable: true,
  hoverable: false,
  showDelay: 100,
  hideDelay: 300,
  offset: 7,
  placement: 'bottom',
  showArrow: false,
  zIndex: 100,
});

const emit = defineEmits<{
  show: [],
  hide: [],
}>();

const isPanelOpen = ref(false);
const showTimeout = ref(null);
const hideTimeout = ref(null);

const reference = ref(null);
const floating = ref(null);
const floatingArrow = ref(null);

const { floatingStyles, middlewareData, update } = useFloating(reference, floating, {
  placement: props.placement,
  middleware: [
    floatOffset(props.offset),
    flip({ fallbackAxisSideDirection: 'end' }),
    shift({ padding: 16 }),
    arrow({ element: floatingArrow })],
  whileElementsMounted: autoUpdate,
});

function onPanelFullyLoaded() {
  // panel is fully rendered and positioned
  update?.();       // recalc position
}

const leftPosition = computed(() => middlewareData.value.arrow?.x != null
  ? `${middlewareData.value.arrow.x}px`
  : '');

const placementPos = computed(() => middlewareData.value.offset?.placement);

const zIndexValue = computed(() => {
  // Check if we're inside a PrimeVue dialog (DynamicDialog or Dialog)
  const isInDialog = reference.value?.closest('.p-dialog, .p-dynamic-dialog');
  // 1200 for dialogs and 100 with default z-index
  return isInDialog ? 1200 : 100;
});

const yPosition = computed(() => {
  if (middlewareData.value.offset?.placement === 'top') {
    return { bottom: '-5px' };
  }
  return { top: '-5px' };
});

const clearTimeouts = () => {
  if (showTimeout.value) {
    clearTimeout(showTimeout.value);
    showTimeout.value = null;
  }
  if (hideTimeout.value) {
    clearTimeout(hideTimeout.value);
    hideTimeout.value = null;
  }
};

const toggle = () => {
  isPanelOpen.value = !isPanelOpen.value;
};

const open = () => {
  clearTimeouts();
  isPanelOpen.value = true;
};

const close = () => {
  clearTimeouts();
  isPanelOpen.value = false;
};

const handleMouseEnter = () => {
  if (!props.hoverable) return;

  clearTimeouts();

  showTimeout.value = setTimeout(() => {
    isPanelOpen.value = true;
  }, props.showDelay);
};

const handleMouseLeave = () => {
  if (!props.hoverable) return;

  clearTimeouts();

  hideTimeout.value = setTimeout(() => {
    isPanelOpen.value = false;
  }, props.hideDelay);
};

const handlePanelMouseEnter = () => {
  if (!props.hoverable) return;

  clearTimeouts();
};

const handlePanelMouseLeave = () => {
  if (!props.hoverable) return;

  clearTimeouts();

  hideTimeout.value = setTimeout(() => {
    isPanelOpen.value = false;
  }, props.hideDelay);
};

const handleClick = () => {
  if (!props.clickable) return;
  toggle();
};


onClickOutside(floating, (event) => {
  // Fix bug with dropdowns in Primevue
  // @ts-expect-error This is necesary because of how primevue handle dropdowns
  if (event?.target?.parentNode?.classList?.contains('p-dropdown-item')) return;
  isPanelOpen.value = false;
}, { ignore: [reference] });


defineExpose({
  toggle,
  open,
  close,
});
</script>

<style scoped>
.panel {
  transition: opacity 0.1s, margin-top 0.2s;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
  margin-top: 5px;
}

.v-enter-to,
.v-leave-from {
  opacity: 1;
  margin-top: 0;
}
</style>
